import React, { useEffect, useState } from "react";
import Action from "../../components/common/Action";
import DebounceSearch from "../../components/common/DebounceSearch";
import RippleLoader from "../../components/loader/RippleLoader";
import CustomTable from "../../components/customTable/CustomTable";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useDispatch, useSelector } from "react-redux";
import Toggles from "../../components/common/Toggles";
import {
  DeleteBanner,
  getBannerSettingList,
  updateBanners,
} from "../../redux/masterSetting/slice";
import AddEditBannerModal from "./AddEditBanner";

const BannerSetting = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [bannerDelete, setBannerDelete] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isType, setIsType] = useState("");
  const [selectedData, setSelectedData] = useState(null);

  // Selector
  const { staffList, loading } = useSelector((store) => ({
    staffList: store?.settingData?.getBannerSettingListStatus?.data?.data,
    loading: store?.settingData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getBannerSettingList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
      })
    );
  }, [searchValue, pageCount, pageLimit]);

  const deleteStaff = () => {
    setIsLoading(true);
    dispatch(DeleteBanner(bannerDelete))
      .then((res) => {
        if (res.type === "DeleteBanner/fulfilled") {
          dispatch(
            getBannerSettingList({
              search: searchValue,
              pageCount: pageCount,
              pageLimit: pageLimit,
            })
          );
        }
        setConfirmDelete(false);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        setConfirmDelete(false);
      });
  };

  const updateStatus = (item, status, isActive) => {
    dispatch(
      updateBanners({
        id: item?.id,
        data: {
          status: status,
          is_active: isActive,
        },
      })
    ).then((res) => {
      if (res.type === "updateBanners/fulfilled") {
        dispatch(
          getBannerSettingList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
          })
        );
      }
    });
  };

  const handleToggle = (item) => {
    const newStatus = item.status === "active" ? "inactive" : "active";
    const isActive = newStatus === "active";

    updateStatus(item, newStatus, isActive);
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        No.
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Title
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <span>Image</span>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        File Name
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Date Range
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Status
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Actions
      </th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(staffList?.data || []).map((item, index) => (
        <tr
          key={item.id}
          className=" shadow-sm text-mgrey hover:bg-lightprimaryorange"
        >
          <td className="px-5 py-3">{index + 1}</td>
          <td className="px-5 py-3">
            {item?.title
              ? item?.title
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "-"}
          </td>
          <td className="px-5 py-3">
            {item?.documents?.url ? (
              <img
                src={item?.documents?.url}
                alt="Document"
                className="w-10 h-10 object-cover"
              />
            ) : (
              "-"
            )}
          </td>
          <td className="px-5 py-3">{item?.documents?.name || "-"}</td>
          <td className="px-5 py-3 text-center">
            {item?.date_range?.trim() ? item.date_range : "-"}
          </td>

          <td className="px-4 py-3 truncate">
            <Toggles
              checked={item?.status === "active"}
              toggleFor={item?.id}
              onChange={() => handleToggle(item)}
            />
          </td>
          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setIsModalOpen(true);
                      setIsType("edit");
                      setSelectedData(item);
                    }}
                  >
                    Edit
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setConfirmDelete(true);
                      setBannerDelete(item?.id);
                    }}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );

  const closeModal = () => {
    setSelectedData(null);
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="space-y-5">
        {/* Search and Export Section */}
        <div className="flex items-center space-x-4 w-full">
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <button
            className="bg-themeBtn text-white flex items-center justify-center px-2 py-[7px] rounded uppercase font-Proxima text-base w-40 shadow-[0_4px_6px_rgba(0,0,0,0.3)]"
            onClick={() => {
              setIsModalOpen(true);
              setIsType("add");
            }}
            type="button"
          >
            + Add Banner
          </button>
        </div>

        {/* Loading or Table */}
        {loading ? (
          <RippleLoader />
        ) : (
          <div className="w-full bg-white shadow rounded-lg">
            <CustomTable
              overflowVisibleLg={true}
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={staffList}
              loading={false}
              showPagination={true}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              current={pageCount}
              total={staffList?.total}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(staffList?.total / pageLimit)}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <ConfirmationModel
          confirmText="Delete"
          onClose={() => setConfirmDelete(false)}
          onConfirm={deleteStaff}
          isLoading={isLoading}
        />
      )}
      {isModalOpen && (
        <div>
          <AddEditBannerModal
            onClose={closeModal}
            isType={isType}
            selectedData={selectedData}
            search={searchValue}
            pageCount={pageCount}
            pageLimit={pageLimit}
          />
        </div>
      )}
    </>
  );
};

export default BannerSetting;
